import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  DASHBOARD_STATUS_EVENT,
  LEGACY_API_ERROR_EVENT,
  emitDashboardStatus,
} from '../utils/statusEvents';
import StatusTray from '../components/StatusTray';

export const DEFAULT_STATUS_DURATION_MS = 5_000;

const StatusContext = createContext(null);

const VALID_TYPES = new Set(['success', 'error', 'warning', 'info', 'loading']);

const randomId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `status-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const titleByType = {
  success: 'Lagret',
  error: 'Noe gikk galt',
  warning: 'Viktig status',
  info: 'Status',
  loading: 'Jobber',
};

export const normalizeStatus = (payload = {}) => {
  const type = VALID_TYPES.has(payload.type) ? payload.type : 'info';
  const message = typeof payload.message === 'string' ? payload.message.trim() : '';
  return {
    id: payload.id || randomId(),
    type,
    title: payload.title || titleByType[type],
    message,
    source: payload.source || null,
    dedupeKey: payload.dedupeKey || null,
    replaceKey: payload.replaceKey || null,
    actionLabel: payload.actionLabel || null,
    actionHref: payload.actionHref || null,
    durationMs: payload.durationMs === undefined
      ? (type === 'loading' ? 0 : DEFAULT_STATUS_DURATION_MS)
      : Number(payload.durationMs),
    createdAt: payload.createdAt || Date.now(),
  };
};

export const statusReducer = (state, action) => {
  switch (action.type) {
    case 'add': {
      const next = normalizeStatus(action.payload);
      if (!next.message && !next.title) return state;

      const matchIndex = state.findIndex((item) => (
        (next.replaceKey && item.replaceKey === next.replaceKey)
        || (next.dedupeKey && item.dedupeKey === next.dedupeKey)
      ));

      if (matchIndex >= 0) {
        const current = state[matchIndex];
        const updated = {
          ...current,
          ...next,
          id: current.id,
          createdAt: Date.now(),
        };
        return [updated, ...state.filter((_, index) => index !== matchIndex)];
      }

      return [next, ...state].slice(0, 8);
    }
    case 'dismiss':
      return state.filter((item) => item.id !== action.id);
    case 'clear':
      return [];
    default:
      return state;
  }
};

export function StatusProvider({ children }) {
  const [statuses, dispatch] = useReducer(statusReducer, []);

  const addStatus = useCallback((payload) => {
    const status = normalizeStatus(payload);
    dispatch({ type: 'add', payload: status });
    return status.id;
  }, []);

  const dismissStatus = useCallback((id) => {
    dispatch({ type: 'dismiss', id });
  }, []);

  const clearStatuses = useCallback(() => {
    dispatch({ type: 'clear' });
  }, []);

  useEffect(() => {
    const handleStatus = (event) => {
      addStatus(event?.detail || {});
    };
    const handleLegacyApiError = (event) => {
      const detail = event?.detail || {};
      addStatus({
        type: 'error',
        title: detail.title || 'Uventet feil',
        message: detail.message || 'Noe gikk galt. Prøv igjen om litt.',
        source: 'api',
        dedupeKey: detail.status ? `api-error:${detail.status}:${detail.message || ''}` : null,
        durationMs: detail.duration ?? DEFAULT_STATUS_DURATION_MS,
      });
    };

    window.addEventListener(DASHBOARD_STATUS_EVENT, handleStatus);
    window.addEventListener(LEGACY_API_ERROR_EVENT, handleLegacyApiError);
    return () => {
      window.removeEventListener(DASHBOARD_STATUS_EVENT, handleStatus);
      window.removeEventListener(LEGACY_API_ERROR_EVENT, handleLegacyApiError);
    };
  }, [addStatus]);

  const value = useMemo(
    () => ({
      statuses,
      addStatus,
      dismissStatus,
      clearStatuses,
      success: (message, options = {}) => addStatus({ ...options, type: 'success', message }),
      error: (message, options = {}) => addStatus({ ...options, type: 'error', message }),
      warning: (message, options = {}) => addStatus({ ...options, type: 'warning', message }),
      info: (message, options = {}) => addStatus({ ...options, type: 'info', message }),
      loading: (message, options = {}) => addStatus({ ...options, type: 'loading', message, durationMs: 0 }),
    }),
    [addStatus, clearStatuses, dismissStatus, statuses],
  );

  return (
    <StatusContext.Provider value={value}>
      {children}
      <StatusTray statuses={statuses} onDismiss={dismissStatus} />
    </StatusContext.Provider>
  );
}

export function useStatus() {
  const context = useContext(StatusContext);
  if (!context) {
    throw new Error('useStatus must be used within a StatusProvider');
  }
  return context;
}

export { emitDashboardStatus };
